import { EventDispatcher } from 'three';
import { autoInjectable, singleton } from 'tsyringe';
import { ISettingsRaw } from '../Models/settings';

import firebase from 'firebase';
import 'firebase/auth';
import 'firebase/database';
import Resources from '../../Resources';
import {
    BehaviorSubject,
    catchError,
    filter,
    from,
    fromEvent,
    map,
    Observable,
    Observer,
    of as observableOf,
    switchMap,
    take,
    throwError,
} from 'rxjs';

const NOTIFICATIONS_PATH = '/notifications/';
const NOTIFICATIONS_UNIVERSAL_PATH = NOTIFICATIONS_PATH + 'receiverId/';
const USER_PERSISTANCE_PATH = '/users/persistance/';
const FIREBASE_PERSISTANCE_PATH = '.info/connected';

@singleton()
@autoInjectable()
export default class FirebaseClient {
    private isReadyObservable: Observable<boolean>;
    private isReadyObserver: Observer<boolean>;
    private isUIReady = false;

    private tokenObservable: Observable<any>;

    private auth: firebase.auth.Auth;
    private database: firebase.database.Database;
    private userLogged = false;
    private initialized: boolean = false;

    private firebaseConnectionSubject = new BehaviorSubject<boolean>(false);

    public constructor(public resources?: Resources) {
        this.removeAllFirebaseStorage();
        this.firebaseConnectionSubject.next(false);
    }
    public async initialize(): Promise<void> {
        console.log('🔥 FirebaseClient: Starting initialization...');

        await new Promise((resolve) => setTimeout(resolve, 0));

        if (!this.resources || !this.resources.isLoaded) {
            console.error('FirebaseClient: Resources not loaded yet!');
            console.log('Resources state:', {
                exists: !!this.resources,
                isLoaded: this.resources?.isLoaded,
                hasItems: !!this.resources?.items,
                itemsKeys: this.resources?.items ? Object.keys(this.resources.items) : [],
            });
            return;
        }

        console.log(' FirebaseClient: Resources validated:', {
            hasSettings: !!this.resources.items?.settings,
            hasUser: !!this.resources.items?.user,
            hasChatToken: !!this.resources.items?.user?.chat_token,
            userId: this.resources.items?.user?.id,
        });

        if (!this.resources.items?.settings) {
            console.warn('FirebaseClient: Settings not available');
            return;
        }

        if (this.resources.items.settings.is_firebase_disabled) {
            console.log('FirebaseClient: Firebase is disabled in settings');
            return;
        }

        if (!this.resources.items.settings.firebase_client_settings) {
            console.warn('FirebaseClient: firebase_client_settings not available');
            return;
        }

        if (!this.resources.items?.user?.chat_token) {
            console.warn('FirebaseClient: User chat_token not available, skipping initialization');
            return;
        }

        try {
            if (!firebase.apps.length) {
                console.log('🔥 FirebaseClient: Initializing Firebase app...');
                firebase.initializeApp(this.resources.items.settings.firebase_client_settings);
            }

            this.auth = firebase.auth();
            this.database = firebase.database();

            console.log('🔥 FirebaseClient: Signing in with custom token...');

            await this.signInWithTokenAsync();

            this.initialized = true;
            this.firebaseConnectionSubject.next(true);
            console.log('✅ FirebaseClient: Successfully initialized and connected');
        } catch (e) {
            console.error('FirebaseClient: Initialization failed', e);
            this.initialized = false;
            this.firebaseConnectionSubject.next(false);
        }
    }

    private async signInWithTokenAsync(): Promise<void> {
        if (!this.resources?.items?.user?.chat_token) {
            throw new Error('Chat token not available');
        }

        const token = this.resources.items.user.chat_token;
        const userId = this.resources.items.user.id;

        try {
            const userCredential = await this.auth.signInWithCustomToken(token);
            this.userLogged = true;

            console.log('Firebase UID:', userCredential.user.uid);
            console.log('User ID from resources:', userId);

            if (userCredential.user.uid !== userId.toString()) {
                console.warn('UID MISMATCH! Token UID !== User ID');
            }
        } catch (error) {
            console.error('FirebaseClient: Sign in failed', error);
            throw error;
        }
    }

    private removeAllFirebaseStorage() {
        try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && key.startsWith('firebase:')) {
                    localStorage.removeItem(key);
                }
            }
        } catch (e) {
            console.warn('⚠️ FirebaseClient: Failed to clear Firebase storage', e);
        }
    }

    private signInWithToken(): Observable<any> {
        return new Observable((subscriber) => {
            if (!this.resources?.items?.user?.chat_token) {
                subscriber.error(new Error('Chat token not available'));
                return;
            }

            const token = this.resources.items.user.chat_token;
            this.auth
                .signInWithCustomToken(token)
                .then(() => {
                    this.userLogged = true;
                    this.initialized = true;
                    subscriber.next();
                    subscriber.complete();
                })
                .catch((error) => {
                    console.error('FirebaseClient: Sign in error', error);
                    subscriber.error(error);
                });
        });
    }

    private setReady() {
        this.isUIReady = true;
        if (this.isReadyObserver) {
            this.isReadyObserver.next(this.isUIReady);
            this.isReadyObserver.complete();
        }
    }

    public isReady(): Observable<boolean> {
        if (this.isUIReady) {
            return observableOf(this.isUIReady);
        } else {
            return this.isReadyObservable;
        }
    }

    public isInitialized(): boolean {
        return this.initialized;
    }

    public updateByPath(
        path: string,
        objectId: string | number | boolean,
        data: Object,
    ): Observable<firebase.database.DataSnapshot> {
        if (!this.initialized) {
            console.warn('FirebaseClient: Not initialized, cannot update path');
            return throwError(() => new Error('Firebase not initialized'));
        }

        const pathRef = this.database.ref(path);
        return Observable.create((observer: Observer<firebase.database.DataSnapshot>) => {
            pathRef
                .orderByChild('object_id')
                .equalTo(objectId)
                .on(
                    'child_added',
                    (response) => {
                        response.ref.update(data).catch((error) => {
                            console.error('FirebaseClient: Update failed', error);
                        });
                        observer.next(null);
                        observer.complete();
                    },
                    (error) => {
                        console.error('FirebaseClient: Query failed', error);
                    },
                );
        });
    }

    public update(objectId: number | string, data) {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot update');
            return throwError(() => new Error('Firebase not initialized'));
        }

        if (!this.resources?.items?.user?.id) {
            console.warn('⚠️ FirebaseClient: User ID not available');
            return throwError(() => new Error('User ID not available'));
        }

        const path = this.getUniversalNotificationPath(this.resources.items.user.id);
        return this.updateByPath(path, objectId, data);
    }

    public updateSomebodyMessage(objectId: number, userId: number) {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot update message');
            return throwError(() => new Error('Firebase not initialized'));
        }

        const path = this.getUniversalNotificationPath(userId);
        return this.updateByPath(path, objectId, {});
    }

    public connectNewNotificationChildAddedHandler(path: string): Observable<firebase.database.DataSnapshot> {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot connect handler');
            return throwError(() => new Error('Firebase not initialized'));
        }

        const pathRef = this.database.ref(path);

        return Observable.create((observer: Observer<firebase.database.DataSnapshot>) => {
            pathRef
                .orderByChild('status')
                .equalTo(true)
                .on(
                    'child_added',
                    (data) => {
                        observer.next(data);
                    },
                    (error) => {
                        console.error('❌ FirebaseClient: Handler connection failed', error);
                    },
                );
        });
    }

    public getData(path: string): Observable<any> {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot get data');
            return throwError(() => new Error('Firebase not initialized'));
        }

        return this.signInWithToken().pipe(
            switchMap(() => {
                const pathRef = this.database.ref(path);
                return new Observable((observer: Observer<any>) => {
                    pathRef.on(
                        'value',
                        (snapshot) => {
                            observer.next(snapshot);
                        },
                        (error) => {
                            console.error('❌ FirebaseClient: Get data failed', error);
                            observer.error(error);
                        },
                    );
                });
            }),
            catchError((error) => {
                console.error('❌ FirebaseClient: getData error', error);
                return throwError(() => error);
            }),
        );
    }

    public getNotificationRef(path) {
        if (!this.initialized || !this.database) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot get notification ref');
            return null;
        }
        return this.database.ref(path);
    }

    public getDataOnce(path: string): Observable<any> {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot get data once');
            return throwError(() => new Error('Firebase not initialized'));
        }

        return this.firebaseConnectionSubject.pipe(
            filter((connected) => connected === true),
            take(1),
            switchMap(() => {
                return new Observable((subscriber) => {
                    const pathRef = this.database.ref(path);

                    pathRef
                        .once('value')
                        .then((snapshot) => {
                            subscriber.next(snapshot.val());
                            subscriber.complete();
                        })
                        .catch((error) => {
                            console.error('❌ FirebaseClient: Get data once failed', error);
                            subscriber.error(error);
                        });
                });
            }),
        );
    }

    public async connectToNotifications(event: (data) => void) {
        if (!this.initialized) {
            console.warn('⚠️ FirebaseClient: Not initialized, cannot connect to notifications');
            return;
        }

        if (!this.resources?.items?.user?.id) {
            console.warn('⚠️ FirebaseClient: User ID not available, cannot connect to notifications');
            return;
        }

        try {
            const pathRef = this.database.ref(this.getUniversalNotificationPath(this.resources.items.user.id));

            pathRef
                .orderByChild('status')
                .equalTo(true)
                .on(
                    'child_added',
                    (data) => {
                        event(data.val());
                    },
                    (error) => {
                        console.error('❌ FirebaseClient: Notification connection failed', error);
                    },
                );
        } catch (error) {
            console.error('❌ FirebaseClient: Failed to connect to notifications', error);
        }
    }

    public getUniversalNotificationPath(userId: number): string {
        return NOTIFICATIONS_UNIVERSAL_PATH + userId;
    }

    public async disconnect(): Promise<void> {
        try {
            if (this.auth && this.userLogged) {
                await this.auth.signOut();
                this.userLogged = false;
            }
            this.initialized = false;
            this.firebaseConnectionSubject.next(false);
            console.log('✅ FirebaseClient: Disconnected');
        } catch (error) {
            console.error('❌ FirebaseClient: Disconnect failed', error);
        }
    }
}
